import { useTranslation } from "react-i18next";

//components
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { SplitButton } from "primereact/splitbutton";
import Avatar from "react-avatar";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { DataTableCard } from "../table-users/styled";

//utils
import { FaEllipsisH, FaExternalLinkAlt, FaLink } from "react-icons/fa";
import { MdOutlineInfo } from "react-icons/md";
import { formatDate } from "../../../../../helpers";
import { colors } from "../../../../../theme/theme";
import { IUserManager } from "../../../../models/IUserManager";
import handleLongText from "../../../../utils/handleLongText/handleLongText";
import { ActionItemLink } from "../action-item-link";
import { StatusTag, TableWrapper, UsageTag } from "./styles";

interface ISubscriptionsTable {
	nodes: IUserManager[];
	sortField: string;
	sortOrder: -1 | 0 | 1;
	loading: boolean;
	handleSort: (event: DataTablePFSEvent) => void;
	handleViewProfile: (node: any) => void;
	handleInactivateSubscription: (node: any) => void;
	handleCancelSubscription: (node: any) => void;
	handleReactivateSubscription: (node: any) => void;
}

export function SubscriptionsTable(props: ISubscriptionsTable) {
	const { t: translation } = useTranslation();
	const { nodes, sortField, sortOrder } = props;

	function nameTemplate(node: IUserManager) {
		let name = node.name ?? "Não informado";
		let responsible = node.username ?? "Nenhum responsável informado";
		return (
			<div className="d-flex align-items-center gap-3">
				<Avatar
					size="28"
					style={{ cursor: "pointer" }}
					round={true}
					name={node.name !== "" ? node.name : "U S"}
					src={node.picture ?? ""}
					color={!node.picture ? colors.mainColors.blueberry : ""}
					onClick={() => props.handleViewProfile(node)}
				/>
				<div className="d-flex flex-column">
					<span
						onClick={() => props.handleViewProfile(node)}
						style={{ cursor: "pointer" }}
						className="font-14"
					>
						<b>{handleLongText(name, 40)}</b>
					</span>
					<span className="text-dark-grey font-12">
						{translation("manager.user.responsible")}:{" "}
						{handleLongText(responsible, 40)}
					</span>
				</div>
			</div>
		);
	}

	/** Indica o status da assinatura, que pode ser Vigente, inativa, ou cancelada */
	function statusTemplate(node: IUserManager) {
		const className =
			node.signature?.status === 1
				? "active"
				: node.signature?.status === 2
					? "inactive"
					: "canceled";

		const status =
			node.signature?.status === 1
				? "Vigente"
				: node.signature?.status === 2
					? "Inativa"
					: "Cancelada";

		return <StatusTag className={className}>{status}</StatusTag>;
	}

	/** Mostra quantos materiais foram usados, e o total disponível para o plano
	 * Quando zero foram utilizados, aparece desabilitado
	 * Quando 80% do total disponível foi utilizado, aparece em amarelo
	 */
	function usageSummaryTemplate(node: IUserManager) {
		if (node.signature?.status !== 1) return <UsageTag>0/0</UsageTag>;

		const percent =
			(node.user_information?.materials_used ?? 0) /
			(node.plan?.materials_max ?? 1);

		const className =
			percent === 0 ? "disabled" : percent >= 0.8 ? "warn" : "";

		return (
			<UsageTag className={className}>{`${
				node.user_information?.materials_used ?? 0
			}/${node.plan?.materials_max ?? 0}`}</UsageTag>
		);
	}

	function infoUseTemplate(node: IUserManager) {
		return (
			<div className="font-12">
				<FaLink className="mt-1 mb-2" />{" "}
				<span>
					{translation("manager.user.usedMaterials")}:{" "}
					{node.user_information?.materials_used
						? node.user_information.materials_used
						: 0}
				</span>
				<br />
				<FaExternalLinkAlt className="mt-1 mb-2" />{" "}
				<span>
					{translation("manager.user.externalMaterials")}:{" "}
					{node.user_information?.external_access
						? node.user_information?.external_access
						: 0}
				</span>
			</div>
		);
	}

	function infoSignatureTemplate(node: IUserManager) {
		let signature = node.signature ? (
			<>
				<span className="font-12">
					{translation("manager.user.hiring")}:{" "}
					{formatDate(node.signature.start)}
				</span>
				<br />
				<span className="font-12">
					{translation("manager.user.dueDate")}:{" "}
					{formatDate(node.signature.end)}
				</span>
			</>
		) : (
			<span className="font-12">Não possui assinatura</span>
		);

		return signature;
	}

	function actionsTemplate(node: IUserManager) {
		let items: any = [];

		items.push({
			template: (
				<ActionItemLink url={`/manager/user/${node.id}`}>
					Visualizar perfil
				</ActionItemLink>
			),
		});

		items.push({
			template: (
				<ActionItemLink
					url={`/manager/user/${node.id}?materialTab=true`}
				>
					{translation("userAccount.usedMaterials")}
				</ActionItemLink>
			),
		});

		items.push({
			template: (
				<ActionItemLink
					url={`/manager/subscriptions/history/${node.id}`}
				>
					{translation("manager.subscription.history")}
				</ActionItemLink>
			),
		});

		if (node.signature && node.signature.status === 1) {
			items.push({
				label: "Inativar assinatura",
				command: () => {
					props.handleInactivateSubscription(node);
				},
			});
		} else if (node.signature && node.signature.status === 2) {
			items.push({
				label: "Reativar assinatura",
				command: () => {
					props.handleReactivateSubscription(node);
				},
			});
		}
		if (node.signature && node.signature.status !== 3) {
			items.push({
				label: "Cancelar assinatura",
				command: (e: any) => {
					props.handleCancelSubscription(node);
				},
			});
		}

		return (
			<OverlayTrigger
				placement="top"
				overlay={<Tooltip>Opções</Tooltip>}
			>
				<div>
					<SplitButton
						label=""
						className="p-button p-component p-button-rounded p-button-text p-button-icon-only"
						buttonClassName="d-none"
						dropdownIcon={<FaEllipsisH />}
						model={items}
						menuClassName="wider-menu"
					/>
				</div>
			</OverlayTrigger>
		);
	}

	const treeTableConfig = [
		{
			field: "name",
			header: <span style={{ paddingLeft: 46 }}>Nome</span>,
			body: nameTemplate,
			sortable: true,
			style: { textAlign: "left", width: "30%" },
		},
		{
			field: "signature_subscriber__status",
			className: "d-none-xs-table d-none-md-table font-14",
			header: "Status",
			body: statusTemplate,
		},
		{
			field: "utilization",
			header: (
				<OverlayTrigger
					overlay={
						<Tooltip>
							<span>Nº de disciplinas utilizadas</span>
							<br></br>
							<span>e limite do plano atual.</span>
						</Tooltip>
					}
				>
					<div className="d-flex align-items-center gap-2">
						<span>Utilização</span>
						<MdOutlineInfo />
					</div>
				</OverlayTrigger>
			),
			body: usageSummaryTemplate,
			sortable: true,
		},
		{
			field: "user_information",
			className: "d-none-xxs-table",
			header: "Informações de uso",
			body: infoUseTemplate,
			sortable: false,
		},

		{
			field: "signature",
			className: "d-none-xxs-table",
			header: "Informações de assinatura",
			body: infoSignatureTemplate,
		},
		{
			field: "actions",
			header: "",
			body: actionsTemplate,
			sortable: false,
			style: { textAlign: "center", width: "7%" },
		},
	];

	return (
		<TableWrapper>
			<DataTableCard>
				<DataTable
					value={nodes}
					sortField={sortField}
					sortOrder={sortOrder}
					onSort={(e) => props.handleSort(e)}
					responsiveLayout="scroll"
					loading={props.loading}
				>
					{treeTableConfig.map((col: any) => {
						return (
							<Column
								key={`key-for-${col.field}`}
								{...col}
							/>
						);
					})}
				</DataTable>
			</DataTableCard>
		</TableWrapper>
	);
}
